import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { FavoriteItem } from '../../types/favorites';
import { LoadingIndicator } from '../shared/LoadingIndicator';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';
import { analytics } from '../../lib/analytics';

interface FavoritesMapProps {
  items: FavoriteItem[];
  config: any;
  translations: Record<string, Record<string, string>>;
  onItemClick: (item: FavoriteItem) => void;
  mainLocation: {
    name: string;
    latitude: number;
    longitude: number;
    fulladress: string;
  };
  categoryColors: Map<string, string>;
  mapInstanceRef: React.MutableRefObject<google.maps.Map | null>;
}

export const FavoritesMap: React.FC<FavoritesMapProps> = ({
  items,
  config,
  translations,
  onItemClick,
  mainLocation,
  categoryColors,
  mapInstanceRef
}) => {
  const { i18n } = useTranslation();
  const mapRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const mainMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const activeMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const markersInitializedRef = useRef<boolean>(false);

  // Filtrer les éléments qui ont des coordonnées
  const mappableItems = React.useMemo(() => 
    items.filter(item => 
      typeof item.data.latitude === 'number' && 
      typeof item.data.longitude === 'number' &&
      !isNaN(item.data.latitude) && 
      !isNaN(item.data.longitude)
    ),
    [items]
  );

  const { isLoaded, error } = useGoogleMaps({
    apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY
  });

  const getTranslatedText = (key: string): string => {
    const translation = translations[key];
    if (!translation) return key;
    return translation[i18n.language] || translation.fr || key;
  };

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !mappableItems.length) return;

    const initMap = async () => {
      try {
        // Créer les limites de la carte
        const bounds = new window.google.maps.LatLngBounds();
        mappableItems.forEach(item => {
          bounds.extend({ 
            lat: item.data.latitude, 
            lng: item.data.longitude 
          });
        });
        bounds.extend({ 
          lat: mainLocation.latitude, 
          lng: mainLocation.longitude 
        });

        // Configurer la carte
        const mapOptions: google.maps.MapOptions = {
          zoom: 12,
          mapTypeControl: false,
          fullscreenControl: false,
          streetViewControl: true,
          clickableIcons: false
        };

        if (config.id_Gmap) {
          mapOptions.mapId = config.id_Gmap;
        }

        // Créer la carte si elle n'existe pas
        if (!mapInstanceRef.current) {
          const map = new window.google.maps.Map(mapRef.current, mapOptions);
          mapInstanceRef.current = map;
        }

        // Ajuster les limites
        mapInstanceRef.current.fitBounds(bounds);

        // Créer la fenêtre d'info si elle n'existe pas
        if (!infoWindowRef.current) {
          infoWindowRef.current = new window.google.maps.InfoWindow({
            disableAutoPan: false
          });

          // Ajouter l'écouteur d'événements une seule fois
          mapInstanceRef.current.addListener('click', () => {
            if (infoWindowRef.current) {
              infoWindowRef.current.close();
              activeMarkerRef.current = null;
            }
          });
        }

        // Créer les marqueurs seulement s'ils n'ont pas été initialisés
        if (!markersInitializedRef.current) {
          await createMarkers();
          markersInitializedRef.current = true;
        }

      } catch (error) {
        // Handle error
      }
    };

    initMap();

    // Nettoyage lors du démontage
    return () => {
      if (mainMarkerRef.current) {
        mainMarkerRef.current.map = null;
      }
      markersRef.current.forEach(marker => {
        marker.map = null;
      });
      markersRef.current = [];
      markersInitializedRef.current = false;
    };
  }, [isLoaded, mappableItems, mainLocation]);

  const createMarkers = async () => {
    if (!mapInstanceRef.current) return;

    try {
      // Supprimer les marqueurs existants
      if (mainMarkerRef.current) {
        mainMarkerRef.current.map = null;
      }
      markersRef.current.forEach(marker => marker.map = null);
      markersRef.current = [];

      // Créer le marqueur principal (hôtel)
      const mainMarkerElement = document.createElement('div');
      mainMarkerElement.innerHTML = `
        <div style="
          width: 54px;
          height: 54px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: transform 0.2s ease-out;
        ">
          <svg viewBox="0 0 24 24" width="100%" height="100%">
            <path 
              fill="${config['major-color']}" 
              stroke="${config['light-color']}" 
              stroke-width="0.5"
              d="M12 2.25a7.76 7.76 0 0 0-7.75 7.75A12.84 12.84 0 0 0 12 22.47a2.58 2.58 0 0 0 1.29-.35 12.81 12.81 0 0 0 6.46-11.12A7.76 7.76 0 0 0 12 2.25zm2.75 11.75a.75.75 0 0 1-1.5 0v-2.25h-2.5v2.25a.75.75 0 0 1-1.5 0v-6a.75.75 0 0 1 1.5 0v2.25h2.5v-2.25a.75.75 0 0 1 1.5 0z"
            />
          </svg>
        </div>
      `;

      mainMarkerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
        map: mapInstanceRef.current,
        position: {
          lat: mainLocation.latitude,
          lng: mainLocation.longitude
        },
        title: mainLocation.name,
        content: mainMarkerElement,
        zIndex: 1000
      });

      // Créer les marqueurs pour les favoris
      mappableItems.forEach(item => {
        const categoryColor = categoryColors.get(item.data.category) || config['medium-color'];
        
        const markerElement = document.createElement('div');
        markerElement.innerHTML = `
          <div style="
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-center;
            cursor: pointer;
            transition: transform 0.2s ease-out;
          ">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="100%" height="100%">
              <path 
                fill="${categoryColor}"
                stroke="${config['light-color']}"
                stroke-width="10"
                d="M384,64.8C305.6-6.6,184.2-1,112.8,77.4c-71.4,78.4-65.8,199.8,12.6,271.2,2,1.8,4.1,3.6,6.1,5.4,44.8,37.3,82.3,82.6,110.4,133.6,2.8,5.2,8.2,8.4,14.1,8.4,5.9,0,11.3-3.2,14.1-8.4l1.3-2.5c28.3-50.4,65.7-95.2,110.2-132.1,80.2-69.3,89-190.4,19.7-270.6-5.4-6.2-11.2-12.1-17.3-17.6Z"
              />
            </svg>
          </div>
        `;

        const marker = new window.google.maps.marker.AdvancedMarkerElement({
          map: mapInstanceRef.current,
          position: { 
            lat: item.data.latitude, 
            lng: item.data.longitude 
          },
          title: item.data.name,
          content: markerElement,
          zIndex: 1
        });

        marker.addListener('click', () => {
          onItemClick(item);
        });

        markersRef.current.push(marker);
      });

    } catch (error) {
      // Handle error
    }
  };

  if (!isLoaded) {
    return <LoadingIndicator config={config} translations={translations} />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-red-50 text-red-600 p-4">
        <div className="text-center mb-4">
          <h3 className="font-semibold mb-2">Erreur de chargement de la carte</h3>
          <p className="text-sm">{error}</p>
          {error.includes('billing') || error.includes('authentication') ? (
            <p className="text-xs mt-2 text-gray-600">
              Veuillez vérifier la configuration de votre clé API Google Maps et que la facturation est activée.
            </p>
          ) : null}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Réessayer
        </button>
      </div>
    );
  }

  // Si aucun élément n'a de coordonnées, afficher un message
  if (mappableItems.length === 0) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 text-gray-500 p-4">
        {translations['no_mappable_favorites']?.[i18n.language] || 'Aucun favori avec des coordonnées géographiques'}
      </div>
    );
  }

  return (
    <div 
      ref={mapRef} 
      className="w-full h-full"
      style={{ 
        position: 'relative',
        zIndex: 1
      }}
    />
  );
};